var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ADD_ATTACHMENTS_BRUSHAWAY_WIDTH, AddCategoryBrushaway, showSuccessToast } from '@ui';
import { translate } from '@utils/i18n';
import { AttachmentsSurface } from '@features/Attachments/AttachmentsSurface';
import { buildFileTypeOptions } from '@features/Attachments/utils/filterOptions';
import { bulkDestroyAttachments, createAttachmentCategory, destroyAttachment, fetchBuildingAttachments } from './services/buildingAttachmentsApi';
import { createDirectUploadTransport } from './services/directUploadTransport';
import { BuildingAttachmentsBulkActions } from './components/BuildingAttachmentsBulkActions';
import { AttachmentNameCell } from './components/AttachmentNameCell';
import { useAttachmentBulkEdits } from './hooks/useAttachmentBulkEdits';
import { buildCategoryEditOptions, buildCategoryOptions, buildVisibilityOptions } from './utils/filterOptions';
import { toBuildingAttachmentRow } from './utils/toBuildingAttachmentRow';
import { buildBuildingColumns, BUILDING_SORTABLE_COLUMNS } from './utils/buildingColumns';
import { BUILDING_DEFAULT_UPLOAD_ATTRIBUTES, buildAddAttachmentsColumns, buildAddAttachmentsLabels, toBuildingQueueFields } from './utils/addAttachmentsConfig';
import { BUILDING_ATTACHMENTS_ENTITY_KEY, BUILDING_ATTACHMENTS_NS, COMMONS_NS, MASS_ACTIONS_NS, PROGRESS_TOAST_NS, UPLOAD_NS, VISIBILITY_EDIT_NS } from './constants';
const t = (key, options) => translate(key, Object.assign({ namespace: BUILDING_ATTACHMENTS_NS }, options));
/**
 * Building attachments as a configuration of the shared `AttachmentsSurface`: the table, filters,
 * toolbar, preview, drawer and delete flow all live there. What stays here is what is genuinely
 * Building's — the category and visibility columns, their inline cells, this surface's endpoints,
 * its bulk edits, and the add-category drawer.
 */
export function BuildingAttachmentsSurface({ buildingId, onAddAttachment, categoryOptions: categoryEditOptionsProp, hasCustomerPortal = true, hasShowAllAttachmentsInCustomerPortal = false, maxUploadSizeBytes, canCreateCategory = false }) {
    // Which surface asked for the add-category drawer. `'queue'` means it was opened from inside the
    // upload drawer, which is still mounted underneath — that is the only case that gets a back
    // arrow and the upload drawer's width (IP-16588). `null` is closed.
    const [addCategorySource, setAddCategorySource] = useState(null);
    const [isSavingCategory, setIsSavingCategory] = useState(false);
    const [categoryError, setCategoryError] = useState(null);
    // Categories created in this session, appended to the server-rendered list so the new one is
    // selectable on other rows too without a page reload.
    const [createdCategories, setCreatedCategories] = useState([]);
    // The requesting cell's own commit callback — see AttachmentCategoryCell's prop doc.
    const applyCreatedCategoryRef = useRef(null);
    const requestAddCategory = useCallback((source) => (apply) => {
        applyCreatedCategoryRef.current = apply;
        setCategoryError(null);
        setAddCategorySource(source);
    }, []);
    const handleRequestAddCategoryFromQueue = useMemo(() => requestAddCategory('queue'), [requestAddCategory]);
    const handleRequestAddCategoryFromTable = useMemo(() => requestAddCategory('table'), [requestAddCategory]);
    const handleCreateCategory = useCallback((values) => __awaiter(this, void 0, void 0, function* () {
        setIsSavingCategory(true);
        setCategoryError(null);
        try {
            // Captured BEFORE the request, not after it. Two things can null this ref while the request
            // is in flight: the drawer's header close button (Cancel is disabled while saving, the X is
            // not), and `setAddCategorySource(null)` below — React 17 does not batch updates made from a
            // Promise callback, so that setState re-renders synchronously and fires `onClose`. Either
            // one used to drop the auto-select on the floor after a successful create.
            const applyToRow = applyCreatedCategoryRef.current;
            const created = yield createAttachmentCategory(values);
            applyCreatedCategoryRef.current = null;
            setCreatedCategories((prev) => [...prev, { id: created.id, label: created.label }]);
            setAddCategorySource(null);
            applyToRow === null || applyToRow === void 0 ? void 0 : applyToRow(created.id);
            showSuccessToast(t('add_category.created_toast', { name: created.label }));
        }
        catch (_a) {
            setCategoryError(t('category_create_error'));
        }
        finally {
            setIsSavingCategory(false);
        }
    }), []);
    const handleCloseAddCategory = useCallback(() => {
        setAddCategorySource(null);
        applyCreatedCategoryRef.current = null;
    }, []);
    const showPortalVisibilityOption = hasCustomerPortal && !hasShowAllAttachmentsInCustomerPortal;
    const uploadLabels = useMemo(() => buildAddAttachmentsLabels(maxUploadSizeBytes !== null && maxUploadSizeBytes !== void 0 ? maxUploadSizeBytes : DEFAULT_MAX_UPLOAD_SIZE_BYTES), [maxUploadSizeBytes]);
    const config = useMemo(() => {
        // Derived from the rows the surface already fetched, so the category options the cells and the
        // bulk menu offer always include anything created in this session.
        const categoryEditOptionsFor = (rows) => [
            ...(categoryEditOptionsProp !== null && categoryEditOptionsProp !== void 0 ? categoryEditOptionsProp : buildCategoryEditOptions(rows, t)),
            ...createdCategories
        ];
        return {
            entityKey: BUILDING_ATTACHMENTS_ENTITY_KEY,
            namespace: BUILDING_ATTACHMENTS_NS,
            progressToastNamespace: PROGRESS_TOAST_NS,
            className: 'building-attachments-table',
            data: {
                fetchAll: fetchBuildingAttachments,
                toRow: toBuildingAttachmentRow,
                destroyOne: destroyAttachment,
                destroyMany: bulkDestroyAttachments
            },
            labels: {
                deleteSuccessToast: (count) => translate('toasts.delete_success', { namespace: MASS_ACTIONS_NS, count }),
                deleteErrorToast: translate('toasts.delete_error', { namespace: MASS_ACTIONS_NS }),
                deleteConfirmBulkTitle: (count) => translate('delete_confirm.bulk_title', { namespace: MASS_ACTIONS_NS, count }),
                deleteConfirmSingleTitle: (fileName) => translate('delete_confirm.single_title', { namespace: MASS_ACTIONS_NS, fileName }),
                deleteConfirmDescription: translate('delete_confirm.description', { namespace: MASS_ACTIONS_NS }),
                deleteConfirmLabel: translate('delete_confirm.confirm_label', { namespace: MASS_ACTIONS_NS }),
                deleteCancelLabel: translate('cancel', { namespace: COMMONS_NS }),
                rowDeleteAriaLabel: (fileName) => translate('row_delete_aria_label', { namespace: MASS_ACTIONS_NS, fileName })
            },
            renderNameCell: (row) => React.createElement(AttachmentNameCell, { item: row, buildingId: buildingId }),
            entityColumns: ({ t: surfaceT, rows }) => buildBuildingColumns({
                translateFn: surfaceT,
                buildingId,
                categoryOptions: categoryEditOptionsFor(rows),
                addCategoryLabel: canCreateCategory ? surfaceT('add_category_action') : undefined,
                onRequestAddCategory: canCreateCategory ? handleRequestAddCategoryFromTable : undefined,
                showPortalVisibilityOption
            }),
            entitySortableColumns: BUILDING_SORTABLE_COLUMNS,
            filters: ({ t: surfaceT }) => [
                {
                    key: 'category',
                    label: surfaceT('filters.category_label'),
                    options: buildCategoryOptions,
                    getValues: (row) => [row.categoryFilterValue]
                },
                {
                    key: 'visibility',
                    label: surfaceT('filters.visibility_label'),
                    options: () => buildVisibilityOptions(surfaceT),
                    getValues: (row) => row.visibilityKeys
                },
                {
                    key: 'file_type',
                    label: surfaceT('filters.file_type_label'),
                    options: buildFileTypeOptions,
                    getValues: (row) => [row.typeFilterValue]
                }
            ],
            // E2E-22: search matches name OR category.
            searchableText: (row) => [row.name, row.categoryLabel],
            renderBulkActions: (ctx) => (React.createElement(BuildingBulkActions, { buildingId: buildingId, ctx: ctx, categoryOptions: categoryEditOptionsFor(ctx.rows), showPortalVisibilityOption: showPortalVisibilityOption })),
            renderExtras: () => (
            /* Opened from the upload drawer, this one has to cover it rather than dock beside it, and
               offer a way back to the queue. Opened from the table's category cell there is nothing
               underneath, so it keeps its own default width and shows only the close button. */
            React.createElement(AddCategoryBrushaway, { open: addCategorySource !== null, minWidth: addCategorySource === 'queue' ? ADD_ATTACHMENTS_BRUSHAWAY_WIDTH : undefined, onBack: addCategorySource === 'queue' ? handleCloseAddCategory : undefined, isSaving: isSavingCategory, errorMessage: categoryError, onErrorDismiss: () => setCategoryError(null), onSubmit: (values) => {
                    void handleCreateCategory(values);
                }, onClose: handleCloseAddCategory, labels: {
                    title: t('add_category.title'),
                    nameLabel: t('add_category.name_label'),
                    namePlaceholder: t('add_category.name_placeholder'),
                    descriptionLabel: t('add_category.description_label'),
                    descriptionPlaceholder: t('add_category.description_placeholder'),
                    submitLabel: t('add_category.submit'),
                    cancelLabel: t('add_category.cancel'),
                    closeLabel: t('add_category.close'),
                    backLabel: t('add_category.back')
                } })),
            upload: {
                transport: createDirectUploadTransport,
                defaultAttributes: BUILDING_DEFAULT_UPLOAD_ATTRIBUTES,
                toFields: toBuildingQueueFields,
                columns: ({ updateItemMetadata, rows }) => buildAddAttachmentsColumns(categoryEditOptionsFor(rows), showPortalVisibilityOption, updateItemMetadata, 
                // Same add-category drawer the main table's category cell opens — the staged row
                // supplies its own apply callback, so the created category lands on the file that
                // asked for it.
                canCreateCategory
                    ? { label: t('add_category_action'), onRequest: handleRequestAddCategoryFromQueue }
                    : undefined),
                labels: uploadLabels,
                successToast: (count) => translate('toasts.success', { namespace: UPLOAD_NS, count })
            }
        };
    }, [
        buildingId,
        canCreateCategory,
        categoryEditOptionsProp,
        createdCategories,
        showPortalVisibilityOption,
        handleRequestAddCategoryFromTable,
        handleRequestAddCategoryFromQueue,
        handleCloseAddCategory,
        handleCreateCategory,
        addCategorySource,
        isSavingCategory,
        categoryError,
        uploadLabels
    ]);
    return (React.createElement(AttachmentsSurface, { config: config, ownerId: buildingId, maxUploadSizeBytes: maxUploadSizeBytes, onAddAttachment: onAddAttachment }));
}
/** Mirrors the surface's own fallback — see `AttachmentsSurface`'s constant of the same name. */
const DEFAULT_MAX_UPLOAD_SIZE_BYTES = 150 * 1024 * 1024;
/**
 * Wraps the bulk-action controls with their mutations. A component rather than inline JSX because
 * `useAttachmentBulkEdits` is a hook and `renderBulkActions` is called during render.
 */
function BuildingBulkActions({ buildingId, ctx, categoryOptions, showPortalVisibilityOption }) {
    const bulkEdits = useAttachmentBulkEdits({
        buildingId,
        selectedIds: ctx.selectedIds,
        clearSelection: ctx.clearSelection,
        invalidate: ctx.invalidate
    });
    // Indeterminate checkbox state for the "Update visibility" popover (E2E-31): mixed
    // `showOnCustomerPortal`/`isPrivate` values across the selected rows.
    const portalValues = ctx.selectedRows.map((row) => row.showOnCustomerPortal);
    const visibilityChecked = portalValues.length > 0 && portalValues.every(Boolean);
    const visibilityIndeterminate = portalValues.length > 0 && !visibilityChecked && portalValues.some(Boolean);
    const privateValues = ctx.selectedRows.map((row) => row.isPrivate);
    const techChecked = privateValues.length > 0 && privateValues.every(Boolean);
    const techIndeterminate = privateValues.length > 0 && !techChecked && privateValues.some(Boolean);
    return (React.createElement(BuildingAttachmentsBulkActions, { updateCategoryLabel: t('toolbar.update_category'), updateVisibilityLabel: t('toolbar.update_visibility'), categoryOptions: categoryOptions, showVisibilityAction: showPortalVisibilityOption, techCheckboxLabel: translate('tech_checkbox_label', { namespace: MASS_ACTIONS_NS }), techChecked: techChecked, techIndeterminate: techIndeterminate, visibilityCheckboxLabel: translate('portal_label', { namespace: VISIBILITY_EDIT_NS }), visibilityChecked: visibilityChecked, visibilityIndeterminate: visibilityIndeterminate, disabled: ctx.selectedIds.length === 0 || ctx.isMutating || bulkEdits.isMutating, onUpdateCategory: bulkEdits.updateCategory, onUpdateTechVisibility: bulkEdits.updateTechVisibility, onUpdateVisibility: bulkEdits.updateVisibility }));
}
